import {
  Box,
  Button,
  Chip,
  Divider,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { useThemePalette } from "common/hooks/theme_palette";

const kindChipSx = {
  mandatory: {
    backgroundColor: "rgba(244, 67, 54, 0.1)",
    color: "#C62828",
  },
  included: {
    backgroundColor: "rgba(33, 150, 243, 0.1)",
    color: "#1565C0",
  },
  additional: {
    backgroundColor: "rgba(255, 152, 0, 0.12)",
    color: "#EF6C00",
  },
};

const statusChipSx = {
  confirmed: { backgroundColor: "rgba(33, 150, 243, 0.12)", color: "#1565C0" },
  in_preparation: {
    backgroundColor: "rgba(255, 152, 0, 0.14)",
    color: "#EF6C00",
  },
  completed: { backgroundColor: "rgba(76, 175, 80, 0.14)", color: "#2E7D32" },
  cancelled: { backgroundColor: "rgba(244, 67, 54, 0.12)", color: "#C62828" },
};

const paymentChipSx = {
  completed: { backgroundColor: "rgba(76, 175, 80, 0.14)", color: "#2E7D32" },
  pending: { backgroundColor: "rgba(255, 152, 0, 0.14)", color: "#EF6C00" },
  failed: { backgroundColor: "rgba(244, 67, 54, 0.12)", color: "#C62828" },
  refunded: { backgroundColor: "rgba(158, 158, 158, 0.18)", color: "#616161" },
};

const MetaItem = ({ label, value }) => (
  <Box>
    <Typography fontSize="12px" color="#8A8A8A" mb={0.5}>
      {label}
    </Typography>
    <Typography fontSize="14px" fontWeight="500" color="#2D2D2D">
      {value ?? "-"}
    </Typography>
  </Box>
);

const PriceRow = ({ label, value, bold, muted }) => (
  <Stack
    direction="row"
    justifyContent="space-between"
    alignItems="center"
    mb={0.75}
  >
    <Typography
      fontSize={bold ? "15px" : "13px"}
      fontWeight={bold ? 700 : 400}
      color={muted ? "#8A8A8A" : "#2D2D2D"}
    >
      {label}
    </Typography>
    <Typography
      fontSize={bold ? "15px" : "13px"}
      fontWeight={bold ? 700 : 500}
      color={muted ? "#8A8A8A" : "#2D2D2D"}
    >
      {value}
    </Typography>
  </Stack>
);

const ServicesSection = ({ title, rows, t, emptyHint, tone }) => {
  const list = rows || [];
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        border: "1px solid #E8E8E8",
        borderRadius: "10px",
        mb: 2,
        ...(tone === "removed"
          ? { backgroundColor: "rgba(244, 67, 54, 0.02)" }
          : tone === "added"
            ? { backgroundColor: "rgba(76, 175, 80, 0.03)" }
            : {}),
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={1.5}
      >
        <Typography fontSize="16px" fontWeight="600">
          {title}
        </Typography>
        <Chip size="small" label={list.length} sx={{ fontWeight: 600 }} />
      </Stack>

      {list.length === 0 ? (
        <Box
          sx={{
            py: 3,
            textAlign: "center",
            borderRadius: "8px",
            backgroundColor: "#FAFAFA",
            border: "1px solid #F0F0F0",
          }}
        >
          <Typography fontSize="13px" color="#8A8A8A">
            {emptyHint || t("events_bookings.no_services")}
          </Typography>
        </Box>
      ) : (
        <Stack gap={1.25}>
          {list.map((s) => (
            <Paper
              key={`${s.item_id}-${s.variant_id}`}
              elevation={0}
              sx={{
                p: 1.75,
                border: "1px solid #E8E8E8",
                borderRadius: "10px",
                backgroundColor: "#fff",
              }}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                gap={1.75}
                alignItems={{ xs: "flex-start", sm: "center" }}
              >
                <Box
                  sx={{
                    width: 64,
                    height: 56,
                    borderRadius: "8px",
                    overflow: "hidden",
                    backgroundColor: "#F5F5F5",
                    border: "1px solid #EEEEEE",
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {s.image ? (
                    <img
                      src={s.image}
                      alt={s.name || ""}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <Typography fontSize="10px" color="#A0A0A0">
                      —
                    </Typography>
                  )}
                </Box>

                <Box flex={1} width="100%">
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    gap={1}
                  >
                    <Box>
                      <Typography fontSize="15px" fontWeight="600">
                        {s.name}
                      </Typography>
                      <Typography fontSize="13px" color="#6A6A6A">
                        {s.option || t("events_packages.no_option")}
                      </Typography>
                      <Stack
                        direction="row"
                        gap={0.75}
                        flexWrap="wrap"
                        mt={0.75}
                      >
                        <Chip
                          size="small"
                          label={t(`events_packages.kinds.${s.kind}`, {
                            defaultValue: s.kind,
                          })}
                          sx={{
                            height: 22,
                            fontSize: "12px",
                            fontWeight: 600,
                            ...(kindChipSx[s.kind] || {}),
                          }}
                        />
                        {s.category?.name && (
                          <Chip
                            size="small"
                            label={s.category.name}
                            sx={{ height: 22, fontSize: "12px" }}
                          />
                        )}
                        {s.locked && (
                          <Chip
                            size="small"
                            label={t("events_packages.locked")}
                            sx={{ height: 22, fontSize: "12px" }}
                          />
                        )}
                        {s.removable && (
                          <Chip
                            size="small"
                            color="info"
                            label={t("events_packages.removable")}
                            sx={{ height: 22, fontSize: "12px" }}
                          />
                        )}
                      </Stack>
                    </Box>
                    <Box textAlign={{ xs: "left", sm: "right" }}>
                      <Typography fontSize="15px" fontWeight="700">
                        {s.amount} {t("products.curr_sar")}
                      </Typography>
                      <Typography fontSize="12px" color="#8A8A8A">
                        {s.quantity} × {s.unit_price}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}
    </Paper>
  );
};

const BookingDetails = ({
  details,
  canUpdate,
  statusOptions,
  onStatusChange,
  statusDraft,
  setStatusDraft,
}) => {
  const { t } = useTranslation();
  const { oliveGreen } = useThemePalette();
  const payment = details?.payment;
  const pricing = details?.pricing;
  const timeline = details?.timeline?.steps || [];
  const currency = pricing?.currency || details?.currency || "SAR";

  const eventTypeName =
    details?.event_type?.name_en ||
    details?.event_type?.name ||
    details?.event_type?.name_ar ||
    "-";
  const packageName =
    details?.package?.name_en ||
    details?.package?.name ||
    details?.package?.name_ar ||
    "-";

  return (
    <Box>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        gap={2}
        mb={2.5}
      >
        <Box>
          <Typography fontSize="22px" fontWeight="600" mb={0.5}>
            {t("events_bookings.details")}
          </Typography>
          <Typography fontSize="14px" color="#797979">
            #{details?.order_number || details?.id}
            {details?.created_at
              ? ` · ${dayjs(details.created_at).format("DD-MM-YYYY HH:mm")}`
              : ""}
          </Typography>
          <Stack direction="row" gap={1} flexWrap="wrap" mt={1}>
            <Chip
              size="small"
              label={t(`events_bookings.statuses.${details?.booking_status}`, {
                defaultValue: details?.booking_status,
              })}
              sx={{
                fontWeight: 600,
                ...(statusChipSx[details?.booking_status] || {}),
              }}
            />
            <Chip
              size="small"
              label={t(
                `events_bookings.payment_statuses.${details?.payment_status}`,
                { defaultValue: details?.payment_status },
              )}
              sx={{
                fontWeight: 600,
                ...(paymentChipSx[details?.payment_status] || {}),
              }}
            />
            {details?.order_status && (
              <Chip
                size="small"
                label={`${t("events_bookings.order_status")}: ${details.order_status}`}
                sx={{ fontWeight: 500 }}
              />
            )}
          </Stack>
        </Box>

        {canUpdate && (
          <Stack
            direction={{ xs: "column", sm: "row" }}
            gap={1}
            alignItems={{ xs: "stretch", sm: "center" }}
          >
            <TextField
              select
              size="small"
              label={t("events_bookings.booking_status")}
              value={statusDraft || details?.booking_status || ""}
              onChange={(e) => setStatusDraft(e.target.value)}
              sx={{ minWidth: 200, backgroundColor: "#fff" }}
            >
              {statusOptions.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                </MenuItem>
              ))}
            </TextField>
            <Button
              variant="contained"
              onClick={() => onStatusChange(statusDraft)}
              disabled={
                !statusDraft || statusDraft === details?.booking_status
              }
              sx={{
                backgroundColor: oliveGreen,
                textTransform: "none",
                "&:hover": { backgroundColor: oliveGreen, opacity: 0.9 },
              }}
            >
              {t("events_bookings.update_status")}
            </Button>
          </Stack>
        )}
      </Stack>

      {timeline.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            border: "1px solid #E8E8E8",
            borderRadius: "10px",
            mb: 2,
          }}
        >
          <Typography fontSize="16px" fontWeight="600" mb={2}>
            {t("events_bookings.timeline")}
          </Typography>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            gap={1.5}
            alignItems={{ xs: "stretch", sm: "center" }}
          >
            {timeline.map((step, index) => {
              const isDone = step.state === "done";
              const isCurrent = step.state === "current";
              return (
                <Stack
                  key={step.key}
                  direction={{ xs: "column", sm: "row" }}
                  alignItems="center"
                  gap={1.5}
                  flex={1}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.5,
                      width: "100%",
                      borderRadius: "10px",
                      border: "1px solid",
                      borderColor: isCurrent
                        ? oliveGreen
                        : isDone
                          ? "#A5D6A7"
                          : "#E8E8E8",
                      backgroundColor: isCurrent
                        ? "rgba(0,0,0,0.02)"
                        : isDone
                          ? "rgba(76, 175, 80, 0.06)"
                          : "#FAFAFA",
                    }}
                  >
                    <Typography
                      fontSize="14px"
                      fontWeight={isCurrent ? 700 : 500}
                    >
                      {step.label}
                    </Typography>
                    <Typography fontSize="12px" color="#8A8A8A" mt={0.25}>
                      {step.at
                        ? dayjs(step.at).format("DD-MM-YYYY HH:mm")
                        : t(`events_bookings.timeline_states.${step.state}`, {
                            defaultValue: step.state,
                          })}
                    </Typography>
                  </Paper>
                  {index < timeline.length - 1 && (
                    <Box
                      sx={{
                        display: { xs: "none", sm: "block" },
                        width: 18,
                        height: 2,
                        backgroundColor: "#E0E0E0",
                        flexShrink: 0,
                      }}
                    />
                  )}
                </Stack>
              );
            })}
          </Stack>
          {details?.timeline?.cancelled && (
            <Typography fontSize="13px" color="#C62828" mt={1.5}>
              {t("events_bookings.cancelled_at")}:{" "}
              {details?.timeline?.cancelled_at
                ? dayjs(details.timeline.cancelled_at).format(
                    "DD-MM-YYYY HH:mm",
                  )
                : "-"}
            </Typography>
          )}
        </Paper>
      )}

      <Stack direction={{ xs: "column", md: "row" }} gap={2} mb={2}>
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            border: "1px solid #E8E8E8",
            borderRadius: "10px",
            flex: 1,
          }}
        >
          <Typography fontSize="16px" fontWeight="600" mb={1.5}>
            {t("events_bookings.customer")}
          </Typography>
          <Stack gap={1.25}>
            <MetaItem
              label={t("events_bookings.customer")}
              value={details?.customer?.name || details?.contact?.name}
            />
            <MetaItem
              label={t("events_bookings.phone")}
              value={`+${details?.customer?.country_code || details?.contact?.country_code || ""} ${details?.customer?.phone_number || details?.contact?.phone || ""}`.trim()}
            />
            {(details?.customer?.email || "").trim() !== "" && (
              <MetaItem
                label={t("events_bookings.email")}
                value={details?.customer?.email}
              />
            )}
            <MetaItem label={t("common.city")} value={details?.city?.name} />
            {details?.notes && (
              <MetaItem label={t("events_bookings.notes")} value={details.notes} />
            )}
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            border: "1px solid #E8E8E8",
            borderRadius: "10px",
            flex: 1,
          }}
        >
          <Typography fontSize="16px" fontWeight="600" mb={1.5}>
            {t("events_bookings.event_info")}
          </Typography>
          <Stack
            direction="row"
            gap={1.5}
            mb={1.5}
            alignItems="center"
          >
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: "8px",
                overflow: "hidden",
                backgroundColor: "#F5F5F5",
                border: "1px solid #EEEEEE",
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {details?.package?.cover_image || details?.event_type?.image ? (
                <img
                  src={
                    details?.package?.cover_image || details?.event_type?.image
                  }
                  alt=""
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              ) : (
                <Typography fontSize="10px" color="#A0A0A0">
                  —
                </Typography>
              )}
            </Box>
            <Box>
              <Typography fontSize="15px" fontWeight="600">
                {packageName}
              </Typography>
              <Typography fontSize="13px" color="#6A6A6A">
                {eventTypeName}
                {details?.package?.level
                  ? ` · ${t(`events_packages.levels.${details.package.level}`, {
                      defaultValue: details.package.level,
                    })}`
                  : ""}
              </Typography>
            </Box>
          </Stack>
          <Divider sx={{ mb: 1.5 }} />
          <Stack
            direction={{ xs: "column", sm: "row" }}
            gap={{ xs: 1.25, sm: 3 }}
            flexWrap="wrap"
          >
            <MetaItem
              label={t("events_bookings.event_date")}
              value={`${details?.event_date || ""} ${details?.event_time || ""}`.trim()}
            />
            <MetaItem
              label={t("events_bookings.guests")}
              value={details?.guests_count}
            />
            <MetaItem
              label={t("events_bookings.total")}
              value={`${details?.total ?? "-"} ${currency}`}
            />
          </Stack>
        </Paper>
      </Stack>

      <ServicesSection
        title={t("events_bookings.services")}
        rows={details?.services}
        t={t}
      />
      <ServicesSection
        title={t("events_bookings.removed_services")}
        rows={details?.removed_services}
        t={t}
        tone="removed"
      />
      <ServicesSection
        title={t("events_bookings.added_services")}
        rows={details?.added_services}
        t={t}
        tone="added"
      />

      <Stack direction={{ xs: "column", md: "row" }} gap={2}>
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            border: "1px solid #E8E8E8",
            borderRadius: "10px",
            flex: 1,
          }}
        >
          <Typography fontSize="16px" fontWeight="600" mb={1.5}>
            {t("events_bookings.pricing")}
          </Typography>
          <PriceRow
            label={t("events_bookings.base_price")}
            value={`${pricing?.base_price ?? "-"} ${currency}`}
          />
          <PriceRow
            label={t("events_bookings.removed_total")}
            value={`- ${pricing?.removed_total ?? "0.00"} ${currency}`}
            muted
          />
          <PriceRow
            label={t("events_bookings.added_total")}
            value={`${pricing?.added_total ?? "0.00"} ${currency}`}
          />
          <PriceRow
            label={t("events_bookings.subtotal")}
            value={`${pricing?.subtotal ?? "-"} ${currency}`}
          />
          <PriceRow
            label={`${t("events_bookings.vat")} (${pricing?.vat_percentage ?? 0}%)`}
            value={`${pricing?.vat_amount ?? "-"} ${currency}`}
          />
          <Divider sx={{ my: 1 }} />
          <PriceRow
            label={t("events_bookings.total")}
            value={`${pricing?.total || details?.total || "-"} ${currency}`}
            bold
          />
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            border: "1px solid #E8E8E8",
            borderRadius: "10px",
            flex: 1,
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={1.5}
          >
            <Typography fontSize="16px" fontWeight="600">
              {t("events_bookings.payment")}
            </Typography>
            <Chip
              size="small"
              label={t(`events_bookings.payment_statuses.${payment?.status}`, {
                defaultValue: payment?.status,
              })}
              sx={{
                fontWeight: 600,
                ...(paymentChipSx[payment?.status] || {}),
              }}
            />
          </Stack>
          <Stack gap={1.25}>
            <MetaItem
              label={t("events_bookings.payment_amount")}
              value={`${payment?.amount ?? "-"} ${payment?.currency || currency}`}
            />
            <MetaItem
              label={t("events_bookings.payment_method")}
              value={payment?.method?.name || "-"}
            />
            <MetaItem
              label={t("events_bookings.payment_reference")}
              value={payment?.reference || "-"}
            />
            {payment?.updated_at && (
              <MetaItem
                label={t("details.updated_at")}
                value={dayjs(payment.updated_at).format("DD-MM-YYYY HH:mm")}
              />
            )}
          </Stack>
        </Paper>
      </Stack>
    </Box>
  );
};

export default BookingDetails;
